import { UserRole } from 'shared/types';

/**
 * Route Access Constants
 *
 * Defines which roles are BLOCKED from accessing each route group.
 * Used by <RestrictedRoute blockedRoles={...}> in App.tsx.
 */

// Only SUPER_ADMIN can access
export const BLOCK_NON_SUPER_ADMIN = [
  UserRole.SAFETY_STAFF,
  UserRole.COMPANY_ADMIN,
  UserRole.CLIENT_APPROVER,
  UserRole.CLIENT_STAFF,
  UserRole.SECURITY,
] as const;

// Only PLATFORM_ROLES (super_admin, safety_staff) can access
export const BLOCK_NON_PLATFORM = [
  UserRole.COMPANY_ADMIN,
  UserRole.CLIENT_APPROVER,
  UserRole.CLIENT_STAFF,
  UserRole.SECURITY,
] as const;

// Only CLIENT_APPROVER can access
export const BLOCK_NON_APPROVER = [
  UserRole.SUPER_ADMIN,
  UserRole.SAFETY_STAFF,
  UserRole.COMPANY_ADMIN,
  UserRole.CLIENT_STAFF,
  UserRole.SECURITY,
] as const;

// Only CLIENT_STAFF can access
export const BLOCK_NON_CLIENT_STAFF = [
  UserRole.SUPER_ADMIN,
  UserRole.SAFETY_STAFF,
  UserRole.COMPANY_ADMIN,
  UserRole.CLIENT_APPROVER,
  UserRole.SECURITY,
] as const;

// Only SECURITY can access
export const BLOCK_NON_SECURITY = [
  UserRole.SUPER_ADMIN,
  UserRole.SAFETY_STAFF,
  UserRole.COMPANY_ADMIN,
  UserRole.CLIENT_APPROVER,
  UserRole.CLIENT_STAFF,
] as const;

// Marketplace browsing surface — accessible to platform + company_admin +
// client_staff. Approvers and security stay out. Provider admins reach this
// from the same `company_admin` role; the BE gates posting/mutating to
// cliente companies (see marketplaceController checkJobPostAccess).
export const BLOCK_NON_MARKETPLACE = [
  UserRole.CLIENT_APPROVER,
  UserRole.SECURITY,
] as const;

// Guest visits & security features: platform + company_admin + security
export const BLOCK_NON_GUEST_ACCESS = [
  UserRole.CLIENT_APPROVER,
  UserRole.CLIENT_STAFF,
] as const;

// Library sub-routes (write paths /new, /edit): platform roles + company_admin only.
// Approvers are kept out of write paths.
export const BLOCK_NON_LIBRARY = [
  UserRole.CLIENT_APPROVER,
  UserRole.CLIENT_STAFF,
  UserRole.SECURITY,
] as const;

// Library detail routes (/:id): same as BLOCK_NON_LIBRARY but allows approver
// and client_staff (Colaborador) read access — approvers need to open
// permits/evaluations they're in the chain of, and a collaborator needs to
// open the Fichas/Evaluaciones/Permisos assigned to them from
// /staff/tareas (StaffTareas.tsx), which reuses this same detail route.
// BE scopes what's visible: approvers to their chain, collaborators to
// their own company (see getServiceCardById/getPermitById/getEvaluationById).
export const BLOCK_NON_LIBRARY_READ = [
  UserRole.SECURITY,
] as const;

// Contractor management: block roles that shouldn't manage providers/contractors.
export const BLOCK_NON_CONTRACTOR_MGMT = [
  UserRole.CLIENT_STAFF,
  UserRole.SECURITY,
] as const;

// My-teams: accessible to most non-security roles.
export const BLOCK_NON_TEAMS = [
  UserRole.SECURITY,
] as const;

// User removal/reactivation request queue: platform roles (decide) +
// company_admin (create/see their own company's requests).
export const BLOCK_NON_REMOVAL_REQUESTS = [
  UserRole.CLIENT_APPROVER,
  UserRole.CLIENT_STAFF,
  UserRole.SECURITY,
] as const;
